/**
 * Single row in the task list popup. Clicking the row navigates to the task's
 * chat; the message-square button injects an "ask about task" prompt; the X
 * button dismisses a terminal entry.
 */
import { useNavigate } from "react-router";
import { FiMessageSquare, FiRefreshCw, FiX } from "react-icons/fi";
import type { TaskDisplayState, TaskStatusValue } from "types/TaskRegistry";
import {
  mcpTaskActionMessage,
  buildLongTaskActionMessage,
  deriveTaskDisplayState,
  shouldStopPolling,
  getTaskKind,
} from "types/TaskRegistry";
import type { Task } from "types/Task";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import { resumeTaskPolling } from "hooks/tasks/taskCacheWrites";
import { useTaskCache } from "hooks/tasks/useTaskCache";
import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
import styles from "./TaskList.module.scss";

interface TaskListEntryProps {
  task: Task;
  onNavigate: () => void;
  onRemove: (jobId: string) => void;
}

const STATUS_LABELS: Record<TaskStatusValue, string> = {
  pending: "pending",
  working: "running",
  completed: "done",
  failed: "failed",
  cancelled: "cancelled",
};

const STATUS_LABEL_STALLED = "stalled";

const ACTION_LABELS: Record<TaskStatusValue, string> = {
  pending: "Ask about status",
  working: "Ask about status",
  completed: "Ask about result",
  failed: "Ask about failure",
  cancelled: "Ask about failure",
};

const ENTRY_CLASS_BY_STATE: Record<TaskDisplayState, string> = {
  working: styles.entryWorking,
  stalled: styles.entryStalled,
  completed: styles.entryCompleted,
  failed: styles.entryFailed,
  cancelled: styles.entryFailed,
};

export default function TaskListEntry({
  task,
  onNavigate,
  onRemove,
}: TaskListEntryProps) {
  const navigate = useNavigate();
  const setTask = useTaskCache();
  // Read the live cache row to catch in-session status updates.
  const liveTask =
    useTasksQuery({
      select: (tasks) => tasks.find((j) => j.id === task.id),
    }).data ?? task;

  const chatName = useThreadsQuery({
    select: (threads) =>
      threads.find((t) => t.id === task.threadId)?.name ?? "",
  }).data;

  const status = liveTask.status;
  const pollExhausted = liveTask.pollExhausted ?? false;

  const handleClick = () => {
    navigate(`/chats/${task.threadId}`);
    onNavigate();
  };

  const handleAsk = (e: React.MouseEvent) => {
    e.stopPropagation();
    const message =
      getTaskKind(liveTask) === "long-task"
        ? buildLongTaskActionMessage(
            status,
            liveTask.jobId,
            liveTask.correlationId ?? undefined
          )
        : mcpTaskActionMessage(status, liveTask.toolName, liveTask.jobId);
    navigate(`/chats/${task.threadId}`, { state: { injectMessage: message } });
    onNavigate();
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    resumeTaskPolling({ jobId: liveTask.jobId, setTask });
  };

  const displayState = deriveTaskDisplayState(status, pollExhausted);
  const entryClass = ENTRY_CLASS_BY_STATE[displayState];

  return (
    <div className={`${styles.entry} ${entryClass}`} onClick={handleClick}>
      <span className={styles.entryDot}>●</span>
      <div className={styles.entryContent}>
        <div className={styles.entryToolName}>
          {liveTask.displayName ?? liveTask.toolName}
        </div>
        <div className={styles.entryChatName}>{chatName}</div>
      </div>
      <span className={styles.entryStatus}>
        {displayState === "stalled"
          ? STATUS_LABEL_STALLED
          : STATUS_LABELS[status] ?? status}
      </span>
      <button
        className={styles.entryAskButton}
        onClick={handleAsk}
        title={ACTION_LABELS[status] ?? "Ask about status"}
        aria-label={ACTION_LABELS[status] ?? "Ask about status"}
      >
        <FiMessageSquare />
      </button>
      {pollExhausted && !shouldStopPolling(status) && (
        <button
          className={styles.entryRefreshButton}
          onClick={handleRefresh}
          title="Resume polling"
          aria-label="Resume polling"
        >
          <FiRefreshCw />
        </button>
      )}
      {shouldStopPolling(status) && (
        <button
          className={styles.entryDismissButton}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(task.jobId);
          }}
          title="Dismiss"
          aria-label="Dismiss task"
        >
          <FiX />
        </button>
      )}
    </div>
  );
}
